import { CcrMFELoadingSkeleton, CcrMFEUnavailable } from '@components/RemoteMicroFrontends/CcrMFEFallbacks';
import { FederatedErrorBoundary } from '@components/RemoteMicroFrontends/FederatedErrorBoundary';
import { useAuthStore } from '@stores/authStore';
import { CoreTypography } from '@youscience/core';
import { lazy, Suspense } from 'react';
import { useLoaderData, useLocation, useParams, RouteObject } from 'react-router-dom';
import { getTenantCrumb } from '@stores/breadCrumbStore';
import { TenantDocument } from '@bpa/core';
import { ErrorPages } from '@components/ui/ErrorPages';
import ErrorPage from '../error-page';
import { CCR_ROUTES } from '.';
import { commonTenantBreadCrumbs } from './commonBreadcrumbs';

const CcrPlannerMFE = lazy(() => import('ccrRemoteMFE/CcrPlannerMFE'));

// --- MFE path: single wildcard route, one prop ---
const getTenantIdFromPath = (path: string) => {
  const segments = path.split('/').filter(Boolean);
  const orgIndex = segments.indexOf('organizations');

  return orgIndex !== -1 ? segments[orgIndex + 1] : undefined;
};

const CcrMFERouteWrapper = () => {
  const { id: tenantId } = useParams();
  const { tenant } = useLoaderData() as { tenant: TenantDocument };
  const { pathname } = useLocation();
  const { currentAccess, userData, impersonatedBy } = useAuthStore((state) => state.authSession);
  const currentPath = pathname.split('/').filter(Boolean).pop();

  if (!CcrPlannerMFE || !tenantId || !userData) return <CcrMFEUnavailable />;

  if (
    currentPath === 'planner' &&
    tenant?.productAvailability?.ccr !== true &&
    currentAccess?.tenantId !== import.meta.env.VITE_ROOT_ORG_ID
  )
    return <ErrorPages errorType={401} homePageLink='' />;

  return (
    <FederatedErrorBoundary moduleName='ccrRemoteMFE' fallback={<CcrMFEUnavailable />}>
      <Suspense fallback={<CcrMFELoadingSkeleton />}>
        <CcrPlannerMFE
          accessDocumentId={currentAccess?.accessDocumentId ?? ''}
          currentPath={currentPath === undefined || currentPath === 'ccr' ? 'settings' : currentPath}
          impersonatedBy={impersonatedBy}
          tenantDocument={tenant}
          userDocument={userData}
          view='full'
        />
      </Suspense>
    </FederatedErrorBoundary>
  );
};

const ccrMFERoute: RouteObject = {
  path: CCR_ROUTES.CCR,
  element: <CcrMFERouteWrapper />,
  errorElement: <ErrorPage />,
  async loader({ params }) {
    const { ccrPlannerMFELoader } = await import('../mfe/loaders/ccrPlannerMFELoader');

    return ccrPlannerMFELoader({ params, request: new Request(window.location.href) });
  },
  handle: {
    crumb: ({ tenant }: { tenant?: TenantDocument }) => {
      const crumbTenant = tenant ?? getTenantCrumb();
      const tenantId = crumbTenant?.tenantId ?? getTenantIdFromPath(window.location.pathname) ?? '';
      const tenantName = crumbTenant?.name ?? '';
      const currentPath = window.location.pathname.split('/').filter(Boolean).pop() ?? '';
      const breadcrumbLabel = currentPath === 'planner' ? 'Manage Course Catalog' : 'CCR';

      return [
        ...commonTenantBreadCrumbs(tenantId, tenantName),
        <CoreTypography color='text.secondary'>{breadcrumbLabel}</CoreTypography>,
      ];
    },
  },
};

// --- Legacy path: two separate routes with loaders (non-MFE fallback) ---

// Export: MFE route when flag is on
export const ccrRoutes: RouteObject[] = [ccrMFERoute];
